import { useContext, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TripPhoto } from '../../types';
import { SelectionContext, type Selection } from './photoSelectionContext';
import { Check, Icon } from '../ui/Icon';

/** `null`: nothing to do with a selection (a viewer on web), so no ticks. */
export function PhotoSelectionProvider({ value, children }: { value: Selection | null; children: ReactNode }) {
  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

interface TileProps {
  photo: TripPhoto;
  onOpen: (photo: TripPhoto) => void;
  className: string;
  style?: CSSProperties;
  /** The photo and whatever is drawn on it. */
  children: ReactNode;
}

/**
 * A photo that opens the viewer, or takes part in the selection. The tick is a
 * sibling of the photo's button, not inside it: a button may not hold another.
 */
export function SelectableTile({ photo, onOpen, className, style, children }: TileProps) {
  const { t } = useTranslation();
  const selection = useContext(SelectionContext);
  const selecting = selection?.selecting ?? false;
  const selected = selection?.isSelected(photo.id) ?? false;

  return (
    <div className="relative group/tile" style={style}>
      <button
        type="button"
        onClick={() => (selecting ? selection?.toggle(photo.id) : onOpen(photo))}
        aria-pressed={selecting ? selected : undefined}
        className={`${className} ${selected ? 'ring-[3px] ring-teal-600 ring-offset-1 dark:ring-offset-slate-900' : ''}`}
      >
        {children}
      </button>
      {selection && (
        <button
          type="button"
          onClick={() => selection.toggle(photo.id)}
          aria-label={t(selected ? 'gallery.selection.unselectPhoto' : 'gallery.selection.selectPhoto')}
          aria-pressed={selected}
          className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center shadow transition-opacity cursor-pointer ${
            selected ? 'bg-teal-600 opacity-100' : 'bg-slate-900/30 hover:bg-slate-900/50'
          } ${selecting || selected ? 'opacity-100' : 'opacity-0 group-hover/tile:opacity-100 focus-visible:opacity-100'}`}
        >
          {selected && <Icon as={Check} size={13} className="text-white" strokeWidth={3} />}
        </button>
      )}
    </div>
  );
}
